import {
  IAP,
  type CompletedOrRefundedOrdersResult,
  type IapCreateOneTimePurchaseOrderResult,
  type IapProductListItem,
  type PendingOrder,
} from '@apps-in-toss/web-framework';
import {
  FREE_RIGHTS,
  LIFETIME_PRODUCT_ID,
  evaluateCommerceAvailability,
  hasVerifiedLifetime,
  type CommerceAvailabilityReason,
  type CommerceRuntimePolicy,
  type EntitlementState,
  type Rights,
} from '../../domain/entitlement';
import { PurchaseUnavailableError, type PurchaseAdapter } from './contracts';
import { apiUrl } from '../api-origin';
import { applyVerifiedRights, currentRights, currentRightsSessionEpoch, resetRights } from '../../domain/entitlement-snapshot';
import { purchaseDeadline } from './deadline';

/**
 * The public Apps in Toss SDK stays inside `src/platform`. The server port is
 * deliberately separate: a browser callback or a client-side order id can
 * never create a premium entitlement on its own.
 */
export interface TossIapRuntime {
  getProductItemList(): Promise<{ products: readonly IapProductListItem[] } | undefined>;
  createOneTimePurchaseOrder(options: {
    options: {
      sku: string;
      processProductGrant(params: { orderId: string }): boolean | Promise<boolean>;
    };
    onEvent(event: { type: 'success'; data: IapCreateOneTimePurchaseOrderResult }): void | Promise<void>;
    onError(error: unknown): void | Promise<void>;
  }): () => void;
  getPendingOrders(): Promise<{ orders: readonly PendingOrder[] } | undefined>;
  completeProductGrant(args: { params: { orderId: string } }): Promise<boolean | undefined>;
  getCompletedOrRefundedOrders(): Promise<CompletedOrRefundedOrdersResult | undefined>;
}

/** Provider-issued Apps in Toss catalog SKU. The business offer id stays
 * carryonpan_lifetime; the SDK only accepts this immutable provider id. */
export const TOSS_LIFETIME_SKU = 'ait.0000064926.be744ed4.57f0db60e2.8843957521' as const;

export interface TossIapEntitlementReadback {
  productId: typeof LIFETIME_PRODUCT_ID;
  state: EntitlementState;
  /** True only after the partner server verified the source of truth. */
  verified: boolean;
}

export interface TossIapOrderReadback extends TossIapEntitlementReadback {
  orderId: string;
}

/**
 * This port is implemented by the partner server. Its implementation must use
 * Apps in Toss's server-to-server mTLS order-status API; no client input is
 * sufficient to return `verified: true`.
 */
export interface TossIapServerVerifier {
  readEntitlement(productId: typeof LIFETIME_PRODUCT_ID): Promise<TossIapEntitlementReadback>;
  verifyAndGrantPendingOrder(input: {
    productId: typeof LIFETIME_PRODUCT_ID;
    orderId: string;
  }): Promise<TossIapOrderReadback>;
  reconcileCompletedOrRefundedOrder(input: {
    productId: typeof LIFETIME_PRODUCT_ID;
    orderId: string;
    status: 'COMPLETED' | 'REFUNDED';
  }): Promise<TossIapOrderReadback>;
}

type TossIapApiResponse = {
  ok?: unknown;
  data?: unknown;
};

export type TossIapServerVerifierOptions = {
  fetchImpl?: typeof fetch;
  /** Obtained only from the explicit purchase/restore login flow. Never persisted. */
  accessToken?: () => Promise<string | null>;
  sessionEpoch?: () => number;
};

const ENTITLEMENT_STATES = new Set<EntitlementState>(['unavailable', 'ready', 'pending', 'paid_ungranted', 'granted', 'restored', 'refunded', 'chargeback', 'revoked']);

function unavailableEntitlement(): TossIapEntitlementReadback {
  return { productId: LIFETIME_PRODUCT_ID, state: 'unavailable', verified: false };
}

function parseEntitlementReadback(value: unknown, expectedOrderId?: string): TossIapEntitlementReadback | TossIapOrderReadback {
  if (!value || typeof value !== 'object') return unavailableEntitlement();
  const data = value as { productId?: unknown; state?: unknown; verified?: unknown; orderId?: unknown };
  if (data.productId !== LIFETIME_PRODUCT_ID || data.verified !== true || typeof data.state !== 'string' || !ENTITLEMENT_STATES.has(data.state as EntitlementState)) return unavailableEntitlement();
  if (expectedOrderId !== undefined) {
    if (data.orderId !== expectedOrderId) return { ...unavailableEntitlement(), orderId: expectedOrderId };
    return { productId: LIFETIME_PRODUCT_ID, state: data.state as EntitlementState, verified: true, orderId: expectedOrderId };
  }
  return { productId: LIFETIME_PRODUCT_ID, state: data.state as EntitlementState, verified: true };
}

async function parseServerResponse(fetchImpl: typeof fetch, path: string, init?: RequestInit, expectedOrderId?: string) {
  try {
    return await purchaseDeadline(async (signal) => {
    const response = await fetchImpl(apiUrl(path), { ...init, credentials: 'omit', redirect: 'error', signal });
    if (!response.ok) return expectedOrderId === undefined ? unavailableEntitlement() : { ...unavailableEntitlement(), orderId: expectedOrderId };
    const payload = await response.json().catch(() => null) as TossIapApiResponse | null;
    if (payload?.ok !== true) return expectedOrderId === undefined ? unavailableEntitlement() : { ...unavailableEntitlement(), orderId: expectedOrderId };
    return parseEntitlementReadback(payload.data, expectedOrderId);
    });
  } catch {
    return expectedOrderId === undefined ? unavailableEntitlement() : { ...unavailableEntitlement(), orderId: expectedOrderId };
  }
}

/**
 * Same-product channel transport, without Web cookies. The current Worker rejects
 * these endpoints with 503, so constructing this verifier never enables IAP;
 * only an enabled mTLS-backed server response with `verified: true` can do so.
 */
export function createTossIapServerVerifier(options: TossIapServerVerifierOptions = {}): TossIapServerVerifier {
  const fetchImpl = options.fetchImpl ?? fetch;
  const request = async (path: string, init?: RequestInit, expectedOrderId?: string) => {
    const responseEpoch = options.sessionEpoch?.() ?? currentRightsSessionEpoch();
    const accessToken = await options.accessToken?.() ?? null;
    // A missing subject binding is not a reason to send an anonymous IAP request.
    if (!accessToken || responseEpoch !== currentRightsSessionEpoch() || (options.sessionEpoch?.() ?? responseEpoch) !== responseEpoch) return expectedOrderId === undefined ? unavailableEntitlement() : { ...unavailableEntitlement(), orderId: expectedOrderId };
    return parseServerResponse(fetchImpl, path, { ...init, headers: { ...init?.headers, authorization: `Bearer ${accessToken}` } }, expectedOrderId);
  };
  const json = (body: unknown): RequestInit => ({
    method: 'POST',
    credentials: 'omit',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  return {
    async readEntitlement(productId) {
      return request(`/api/iap/entitlements/me?productId=${encodeURIComponent(productId)}`) as Promise<TossIapEntitlementReadback>;
    },
    async verifyAndGrantPendingOrder(input) {
      return request('/api/iap/orders/verify-and-grant', json(input), input.orderId) as Promise<TossIapOrderReadback>;
    },
    async reconcileCompletedOrRefundedOrder(input) {
      return request('/api/iap/orders/reconcile', json(input), input.orderId) as Promise<TossIapOrderReadback>;
    },
  };
}

export type TossIapCatalogObservation = {
  available: boolean;
  reason: CommerceAvailabilityReason;
  product: Extract<IapProductListItem, { type: 'NON_CONSUMABLE' }> | null;
  serverVerified: boolean;
};

export interface TossIapAdapter extends PurchaseAdapter {
  cancel?(): void;
  /**
   * Reads the live SDK catalog and then a server entitlement readback. This is
   * the only operation that can make the purchase CTA eligible to render.
   */
  refreshAvailability(): Promise<TossIapCatalogObservation>;
  /** Authenticated ledger state for the Premium surface; never a client order callback. */
  readStatus(): Promise<TossIapEntitlementReadback>;
  /** A restore may apply rights only after the complete SDK history is verified. */
  restoreStatus(): Promise<{ complete: boolean; readback: TossIapEntitlementReadback }>;
}

export type TossIapAdapterOptions = {
  policy: CommerceRuntimePolicy;
  runtime?: TossIapRuntime;
  serverVerifier?: TossIapServerVerifier;
  /** The concrete session owner changes this on login/logout/account switch. */
  sessionEpoch?: () => number;
  /** Strict server binding capability permits login/restore; it is not provider-success evidence. */
  authConfigured?: boolean;
};

function isObservedLifetimeProduct(product: IapProductListItem): product is Extract<IapProductListItem, { type: 'NON_CONSUMABLE' }> {
  return product.sku === TOSS_LIFETIME_SKU && product.type === 'NON_CONSUMABLE';
}

function validEntitlement(readback: TossIapEntitlementReadback): boolean {
  return readback.verified === true && readback.productId === LIFETIME_PRODUCT_ID;
}

function validOrder(readback: TossIapOrderReadback, orderId: string): boolean {
  return validEntitlement(readback) && readback.orderId === orderId;
}

/**
 * Apps in Toss IAP adapter with a safe default. It intentionally does not
 * accept the generic client-side PurchaseProvider: the Apps in Toss runtime
 * must prove the catalog, and an mTLS-backed server must prove every order.
 */
export function createTossIapAdapter(options: TossIapAdapterOptions): TossIapAdapter {
  const runtime = options.runtime ?? IAP;
  let observedProduct: Extract<IapProductListItem, { type: 'NON_CONSUMABLE' }> | null = null;
  let observedServerVerified = false;
  let stopPurchase: (() => void) | undefined;
  let generation = 0;

  function baseReason(): CommerceAvailabilityReason {
    if (options.authConfigured && options.policy.providerEnabled && !options.policy.killSwitch) {
      if (!options.policy.productObserved) return 'product_unobserved';
      return options.serverVerifier ? 'ready' : 'provider_disabled';
    }
    const policy = evaluateCommerceAvailability(options.policy);
    if (!policy.available) return policy.reason;
    if (!options.serverVerifier) return 'provider_disabled';
    return 'ready';
  }

  function currentObservation(): TossIapCatalogObservation {
    const reason = baseReason();
    if (reason !== 'ready') return { available: false, reason, product: null, serverVerified: false };
    if (!observedProduct) return { available: false, reason: 'product_unobserved', product: null, serverVerified: false };
    if (!observedServerVerified) return { available: false, reason: 'provider_outage', product: null, serverVerified: false };
    return { available: true, reason: 'ready', product: observedProduct, serverVerified: true };
  }

  async function refreshAvailability(): Promise<TossIapCatalogObservation> {
    const responseEpoch = options.sessionEpoch?.() ?? currentRightsSessionEpoch();
    const responseGeneration = generation;
    observedProduct = null;
    observedServerVerified = false;
    const reason = baseReason();
    // This branch is the production default: no SDK or server request occurs.
    if (reason !== 'ready') return currentObservation();

    try {
      const catalog = await purchaseDeadline(() => runtime.getProductItemList());
      const product = catalog?.products.find(isObservedLifetimeProduct) ?? null;
      if (!product || !ownsEpoch(responseEpoch, responseGeneration)) return currentObservation();
      const server = options.serverVerifier;
      if (!server) return currentObservation();
      const entitlement = await server.readEntitlement(LIFETIME_PRODUCT_ID);
      if (!ownsEpoch(responseEpoch, responseGeneration) || !validEntitlement(entitlement)) return { available: false, reason: 'provider_outage', product: null, serverVerified: false };
      observedProduct = product;
      observedServerVerified = true;
      return currentObservation();
    } catch {
      return { available: false, reason: 'provider_outage', product: null, serverVerified: false };
    }
  }

  async function requireObservedAvailability() {
    const observation = await refreshAvailability();
    if (!observation.available) throw new PurchaseUnavailableError(observation.reason);
    return observation.product;
  }

  async function verifiedRights(): Promise<Rights> {
    const outcome = await restoreStatus();
    return outcome.complete ? currentRights() : FREE_RIGHTS;
  }

  const ownsEpoch = (responseEpoch: number, responseGeneration = generation) => responseGeneration === generation && responseEpoch === currentRightsSessionEpoch() && (options.sessionEpoch?.() ?? responseEpoch) === responseEpoch;
  const resetOwnedRights = (responseEpoch: number, responseGeneration: number) => { if (ownsEpoch(responseEpoch, responseGeneration)) resetRights(responseEpoch); };
  async function ledgerReadStatus(responseEpoch = options.sessionEpoch?.() ?? currentRightsSessionEpoch(), responseGeneration = generation): Promise<TossIapEntitlementReadback> {
    try {
      const server = options.serverVerifier;
      if (!server) {
        resetOwnedRights(responseEpoch, responseGeneration);
        return unavailableEntitlement();
      }
      if (!ownsEpoch(responseEpoch, responseGeneration)) return unavailableEntitlement();
      const entitlement = await server.readEntitlement(LIFETIME_PRODUCT_ID);
      if (!ownsEpoch(responseEpoch, responseGeneration) || !validEntitlement(entitlement)) {
        resetOwnedRights(responseEpoch, responseGeneration);
        return unavailableEntitlement();
      }
      applyVerifiedRights(entitlement, responseEpoch);
      return entitlement;
    } catch {
      resetOwnedRights(responseEpoch, responseGeneration);
      return unavailableEntitlement();
    }
  }

  async function restoreStatus(): Promise<{ complete: boolean; readback: TossIapEntitlementReadback }> {
    const responseEpoch = options.sessionEpoch?.() ?? currentRightsSessionEpoch();
    const responseGeneration = generation;
    const failed = () => ({ complete: false, readback: unavailableEntitlement() });
    try {
      if (!options.serverVerifier) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
      const server = options.serverVerifier;
      if (!server) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
      // Bind the restore flow to the authenticated ledger before accepting any
      // SDK order ids. Catalog availability is intentionally not required.
      if (!validEntitlement(await server.readEntitlement(LIFETIME_PRODUCT_ID)) || !ownsEpoch(responseEpoch, responseGeneration)) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
      const pending = await purchaseDeadline(() => runtime.getPendingOrders());
      if (!ownsEpoch(responseEpoch, responseGeneration)) return failed();
      const history = await purchaseDeadline(() => runtime.getCompletedOrRefundedOrders());
      const orderShape = (value: unknown): boolean => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
        const order = value as { orderId?: unknown; sku?: unknown };
        return typeof order.orderId === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(order.orderId) && typeof order.sku === 'string' && order.sku.length > 0;
      };
      // Validate the whole response before any host acknowledgement, even a later malformed element.
      if (!ownsEpoch(responseEpoch, responseGeneration) || !pending || !Array.isArray(pending.orders) || !pending.orders.every(orderShape) || !history || !Array.isArray(history.orders) || !history.orders.every((order) => orderShape(order) && ['COMPLETED', 'REFUNDED'].includes(order.status)) || history.hasNext !== false || (history.nextKey !== undefined && history.nextKey !== null)) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
      for (const order of pending.orders) {
        if (!ownsEpoch(responseEpoch, responseGeneration)) return failed();
        if (order.sku !== TOSS_LIFETIME_SKU) continue;
        const granted = await server.verifyAndGrantPendingOrder({ productId: LIFETIME_PRODUCT_ID, orderId: order.orderId });
        if (!ownsEpoch(responseEpoch, responseGeneration) || !validOrder(granted, order.orderId) || !hasVerifiedLifetime(granted.state)) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
        // The host is acknowledged only after the server has persisted a verified grant.
        if ((await purchaseDeadline(() => runtime.completeProductGrant({ params: { orderId: order.orderId } }))) !== true || !ownsEpoch(responseEpoch, responseGeneration)) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
      }

      for (const order of history.orders) {
        if (!ownsEpoch(responseEpoch, responseGeneration)) return failed();
        if (order.sku !== TOSS_LIFETIME_SKU) continue;
        const reconciled = await server.reconcileCompletedOrRefundedOrder({
          productId: LIFETIME_PRODUCT_ID,
          orderId: order.orderId,
          status: order.status,
        });
        if (!ownsEpoch(responseEpoch, responseGeneration) || !validOrder(reconciled, order.orderId)) { resetOwnedRights(responseEpoch, responseGeneration); return failed(); }
      }
      // History is only a reconciliation input. The server ledger is still the
      // sole entitlement source, including after REFUNDED observations.
      const readback = await ledgerReadStatus(responseEpoch, responseGeneration);
      return validEntitlement(readback) ? { complete: true, readback } : failed();
    } catch {
      resetOwnedRights(responseEpoch, responseGeneration);
      return failed();
    }
  }

  /** A ledger response alone never projects paid rights: complete history is mandatory. */
  async function readStatus(): Promise<TossIapEntitlementReadback> {
    return (await restoreStatus()).readback;
  }

  async function restoreRights(): Promise<Rights> {
    const result = await restoreStatus();
    return result.complete ? currentRights() : FREE_RIGHTS;
  }

  return {
    channel: 'apps_in_toss',
    productId: LIFETIME_PRODUCT_ID,
    get available() {
      return currentObservation().available;
    },
    get unavailableReason() {
      return currentObservation().reason;
    },
    rightsBeforeReadback: FREE_RIGHTS,
    refreshAvailability,
    readStatus,
    restoreStatus,
    async start() {
      const responseEpoch = options.sessionEpoch?.() ?? currentRightsSessionEpoch();
    const responseGeneration = generation;
      await requireObservedAvailability();
      if (!ownsEpoch(responseEpoch, responseGeneration)) throw new Error('PURCHASE_CANCELLED');
      const server = options.serverVerifier;
      if (!server) throw new PurchaseUnavailableError('provider_disabled');
      return new Promise<{ orderId: string; state: 'pending' }>((resolve, reject) => {
        let cleanup: (() => void) | undefined;
        let cleanupRequested = false;
        let pendingResolved = false;
        let ended = false;
        let finishWithError: (error: unknown) => void = () => undefined;
        const onPageHide = () => finishWithError(new Error('purchase view closed before completion'));

        const clear = () => {
          cleanupRequested = true;
          cleanup?.();
          cleanup = undefined;
          if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
        };
        finishWithError = (error) => {
          clear();
          if (ended) return;
          ended = true;
          if (!pendingResolved) reject(error);
        };
        stopPurchase = () => finishWithError(new Error('PURCHASE_CANCELLED'));
        const resolvePending = (orderId: string) => {
          if (pendingResolved) return;
          pendingResolved = true;
          resolve({ orderId, state: 'pending' });
        };

        try {
          cleanup = runtime.createOneTimePurchaseOrder({
            options: {
              sku: TOSS_LIFETIME_SKU,
              processProductGrant: async ({ orderId }) => {
                if (ended || !ownsEpoch(responseEpoch, responseGeneration)) return false;
                // A pending client callback is intentionally not a grant.
                resolvePending(orderId);
                try {
                  const grant = await server.verifyAndGrantPendingOrder({ productId: LIFETIME_PRODUCT_ID, orderId });
                  const approved = !ended && ownsEpoch(responseEpoch, responseGeneration) && validOrder(grant, orderId) && hasVerifiedLifetime(grant.state);
                  if (!approved) clear();
                  return approved;
                } catch {
                  clear();
                  return false;
                }
              },
            },
            onEvent: async () => {
              clear();
              if (!pendingResolved) finishWithError(new Error('purchase succeeded without a verified pending grant'));
            },
            onError: async (error) => finishWithError(error),
          });
          if (cleanupRequested) clear();
          else if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide, { once: true });
        } catch (error) {
          finishWithError(error);
        }
      });
    },
    cancel() { generation += 1; observedProduct = null; observedServerVerified = false; stopPurchase?.(); stopPurchase = undefined; },
    restore: restoreRights,
    readRights: verifiedRights,
  };
}
